import { format, isBefore } from 'date-fns';
import { useCallback, useEffect, useState } from 'react';
import ContentLoader from 'react-content-loader';
import { Link, useParams } from 'react-router-dom';
import Logo from '../../../../../assets/images/brand/only-logo.png';
import { useUserStore } from '../../../../../modules/Auth/stores/authStore';
import { CourseContentModal } from '../../../../../modules/Courses/components/CourseContent/CourseContentModal';
import { useEssayTheme } from '../../../../../modules/Courses/hooks/useEssayTheme';
import { useFetchContent } from '../../../../../modules/Courses/hooks/useFetchContent';
import { useFetchCourseDetails } from '../../../../../modules/Courses/hooks/useFetchCourseDetails';
import { useFetchProgram } from '../../../../../modules/Courses/hooks/useFetchProgram';
import { useLoadCourse } from '../../../../../modules/Courses/hooks/useLoadCourse';
import { useMarkContentAsSelected } from '../../../../../modules/Courses/hooks/useMarkContentAsSelected';
import { CourseWeek } from '../../../../../modules/Courses/models/Course.model';
import { useCoursesStore } from '../../../../../modules/Courses/stores/courseStore';
import { useEssayCourseStore } from '../../../../../modules/Courses/stores/essayStore';
import { Button } from '../../../../components/atoms/Button';
import DropDown from '../../../../components/molecules/Dropdown';
import { Typography } from '../../../../components/molecules/Typography';
import { Breadcrumbs } from '../../../../components/organism/Breadcrumbs';
import DropdownButton from '../DropdownButton';
import { WeekProgramModal } from '../WeekProgramModal';
import { getDisciplineSlugByName } from '../../../../utils/discipline';
import { Tag } from '../../../../components/atoms/Tag';

interface ICourseMenu {
  isLocked?: boolean;
}

export default function CourseMenu({ isLocked }: ICourseMenu) {
  const { slug } = useParams();
  const { mutate } = useEssayTheme();
  const { setIsWriting, isWriting } = useEssayCourseStore();

  const loadCourse = useLoadCourse(slug ?? '');
  const fetchCourseDetails = useFetchCourseDetails(slug ?? '');
  const fetchProgram = useFetchProgram();

  const markContentAsSelected = useMarkContentAsSelected();
  const fetchContent = useFetchContent();
  const { loadedCourse, setLoadedCourse, course, setIsLoadingContent } =
    useCoursesStore();

  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isWeekProgramModalOpen, setIsWeekProgramModalOpen] =
    useState<boolean>(false);
  const [isModalCourseContentOpen, setIsModalCourseContentOpen] =
    useState<boolean>(false);
  const [selectedWeek, setSelectedWeek] = useState<CourseWeek | null>(null);
  const [isContentZero, setIsContentZero] = useState<boolean>(false);

  const [nextContent, setNextContent] = useState<any>(null);

  const { user } = useUserStore();

  useEffect(() => {
    if (!loadedCourse) {
      return;
    }

    if (!loadedCourse.selectedContent) {
      handleSelectWeek(loadedCourse.selectedProgramObject);
    }

    setSelectedWeek(loadedCourse?.selectedProgramObject);

    if (isBefore(new Date(), new Date(loadedCourse.startDate))) {
      setIsContentZero(true);
    } else {
      setIsContentZero(false);
    }

    if (loadedCourse.selectedContentObject.model === 'EssayActivity') {
      mutate(loadedCourse.selectedContentObject.essayTheme.slug);
    }

    setNextContent(getNextContent(loadedCourse));
  }, [loadedCourse]);

  const getNextContent = useCallback(
    (course: any) => {
      const currentProgram = course?.selectedProgramObject;
      const currentContent = course?.selectedContentObject;

      const currentProgramAreaContent = getProgramContentListByArea(
        currentProgram,
        currentContent.area?.slug
      );

      const currentContentIndex = currentProgramAreaContent.findIndex(
        (content: any) => content?._contentId === currentContent?._contentId
      );

      const nextContent = currentProgramAreaContent[currentContentIndex + 1];

      if (!nextContent) {
        return null;
      }

      return nextContent;
    },
    [loadedCourse]
  );

  const getProgramContentListByArea = useCallback(
    (selectedProgramObject: any, slug: string) => {
      return selectedProgramObject.content
        .filter(
          (content: any) =>
            content?.area?.slug === slug &&
            content.category === 'normal' &&
            content.type !== 'Label'
        )
        .sort((a: any, b: any) => a.order - b.order);
    },
    [loadedCourse?.selectedProgramObject]
  );

  async function handleSelectWeek(week: CourseWeek) {
    if (!course) {
      return;
    }

    setSelectedWeek(week);
    setIsOpen(false);
    setIsModalCourseContentOpen(false);

    fetchProgram.mutate(
      {
        courseId: course._id,
        programId: week._id || week.id,
      },
      {
        onSuccess: (data) => {
          const newLoadedCourse = {
            ...loadedCourse,
            selectedProgramObject: data,
            selectedProgram: data.id,
          };

          const lastSelectedAreaLabel = localStorage.getItem(
            `at:lastSelectedDiscipline:${course._id}`
          );

          if (lastSelectedAreaLabel) {
            const selectedAreaSlug = getDisciplineSlugByName(
              lastSelectedAreaLabel || ''
            );
            const areaContentList = getProgramContentListByArea(
              data,
              selectedAreaSlug
            );
            const firstContent = areaContentList.find(
              (content: any) => content.type !== 'Label'
            );

            if (firstContent) {
              handleFirstContent(
                firstContent.contentId,
                data.id,
                newLoadedCourse
              );
              return;
            }
          }

          handleFirstContent(data.selectedContent, data.id, newLoadedCourse);
        },
      }
    );
  }

  function handleFirstContent(
    contentId: string,
    programId: string,
    course: any
  ) {
    const contentParams = {
      courseId: course.id,
      programId: programId,
      contentId: contentId,
    };

    markContentAsSelected.mutate(contentParams);

    setIsLoadingContent(true);
    fetchContent.mutate(contentParams, {
      onSuccess: (data: any) => {
        const newLoadedCourse = {
          ...course,
          selectedContentObject: data,
          selectedContent: data._contentId,
        };
        setLoadedCourse(newLoadedCourse);
        setIsLoadingContent(false);
      },
    });
  }

  function handleNextContent() {
    if (!nextContent) {
      return;
    }

    const contentParams = {
      courseId: loadedCourse.id,
      programId: loadedCourse.selectedProgram,
      contentId: nextContent._contentId,
    };

    markContentAsSelected.mutate(contentParams);

    setIsLoadingContent(true);
    fetchContent.mutate(contentParams, {
      onSuccess: (data: any) => {
        const newLoadedCourse = {
          ...loadedCourse,
          selectedContentObject: data,
          selectedContent: data._contentId,
        };
        setLoadedCourse(newLoadedCourse);
        setIsLoadingContent(false);
      },
    });
  }

  function handleOpenContentModal() {
    setIsModalCourseContentOpen(true);

    setTimeout(() => {
      const selectedCourseContent = window.document.getElementById(
        loadedCourse.selectedContent
      );

      selectedCourseContent?.scrollIntoView({
        behavior: 'auto',
        block: 'center',
      });
    }, 100);
  }

  if (loadCourse.isLoading || fetchCourseDetails.isLoading) {
    const random = Math.random() * (1 - 0.7) + 0.7;

    return (
      <div className='px-12'>
        <ContentLoader viewBox='0 0 1000 40'>
          <rect x='0' y='15' rx='4' ry='4' width='6' height='6.4' />
          <rect x='34' y='13' rx='6' ry='6' width={200 * random} height='12' />
          <rect x='633' y='13' rx='6' ry='6' width={23 * random} height='12' />
          <rect x='653' y='13' rx='6' ry='6' width={78 * random} height='12' />
          <rect x='755' y='13' rx='6' ry='6' width={117 * random} height='12' />
          <rect x='938' y='13' rx='6' ry='6' width={83 * random} height='12' />

          <rect x='0' y='39' rx='6' ry='6' width='1060' height='.3' />
        </ContentLoader>
      </div>
    );
  }

  const formHref = () => {
    if (course && course.kind) {
      return course.kind === 'regular'
        ? '/cursos/regulares'
        : course.kind === 'regional'
          ? '/regionais'
          : '/cursos/complementares';
    }

    return '';
  };

  const buildBreadcrumb = () => {
    const activeContent = loadedCourse
      ? loadedCourse.selectedContentObject
      : null;
    if (activeContent && activeContent.type === 'EssayActivity') {
      return [
        {
          label: '',
          href: '#',
        },
        {
          label: 'Cursos',
          href: formHref(),
        },
        {
          label: selectedWeek ? selectedWeek.title : 'semana',
          href: '#',
          onClick: () => setIsWriting(false),
        },
        {
          label: isWriting ? 'Enviar redação' : 'Tema da redação',
          href: '#',
        },
      ];
    }

    return [
      {
        label: '',
        href: '#',
      },
      {
        label: 'Cursos',
        href: formHref(),
      },
      {
        label: selectedWeek ? selectedWeek.title : 'semana',
        href: '#',
      },
    ];
  };

  return (
    <div className='xg:px-[89px] flex h-[78px] w-full flex-row bg-color-lightest py-5 shadow-level-1 xs:px-4 md:px-6 lg:px-8'>
      <div className='h-full flex-row items-center justify-start gap-lg xs:hidden lg:flex lg:w-2/6'>
        <Link to='/'>
          <img
            src={Logo}
            className='xs:hidden xs:h-[24px] xs:w-[16.89px] md:h-[38px] md:w-[26.29px] lg:block'
            alt='Logo black'
          />
        </Link>
        <Breadcrumbs items={buildBreadcrumb()} />
      </div>

      <div className='flex h-full flex-row items-center xs:w-1/2 xs:justify-start lg:w-2/6 lg:justify-center'>
        {selectedWeek?.isBuilding && 
          <Typography size='small' className='text-mono-color-dark'>
            Em construção
          </Typography>
        }
        <Button allowReadOnly
          variant='text'
          LeftIcon={'chevron_left'}
          className='flex items-center justify-center p-0 lg:hidden'
          classNameIcon='text-2xl md:text-4xl'
          onlyIcon
          asLink
          to={formHref()}
        />

        <DropdownButton
          isOpen={isOpen}
          week={selectedWeek}
          setIsOpen={(open) => setIsOpen(open)}
          disabled={isContentZero || isLocked}
        />

        {course && course.kind === 'regular' && (
          <Button allowReadOnly
            variant='text'
            size='small'
            className='xs:hidden md:hidden lg:block'
            onClick={() => setIsWeekProgramModalOpen(true)}
            disabled={isContentZero}
          >
            {`${selectedWeek ? selectedWeek.averageDuration : '0h'} de videoaula`}
          </Button>
        )}

        <DropDown
          id={`week-select`}
          position='bottom-start'
          arrow
          onClick
          afterHide={(status) => setIsOpen(status)}
          afterShow={() => {
            const selectedItem = document.querySelector(`#selectedWeek`);
            selectedItem?.scrollIntoView({ behavior: 'auto' });
          }}
          style={
            user?.isDarkMode
              ? { backgroundColor: '#000C29' }
              : { backgroundColor: '#ffffff' }
          }
          //@todo: ver como utilizar os design tokens}
        >
          <div className='max-h-[45vh] overflow-y-auto'>
            {course &&
              course.programs.map((it: any, i) => {
                return (
                  <Button allowReadOnly
                    key={i}
                    id={
                      selectedWeek && it._id === selectedWeek.id
                        ? 'selectedWeek'
                        : ''
                    }
                    variant={
                      selectedWeek && it._id === selectedWeek.id
                        ? 'selected'
                        : 'hoverable'
                    }
                    full='menu'
                    onClick={() => handleSelectWeek(it)}
                    disabled={!it.isAvailable}
                    className='xs:w-fit lg:w-[335px]'
                  >
                    <div className='flex flex-row items-center gap-lg text-left'>
                      <Typography type='lead' weight='bold'>
                        {it.title}
                      </Typography>
                      {course && course.kind === 'regular' && (
                        <Typography size='small' variant='italic'>
                          {format(new Date(it.startDate), 'dd/MM/yyyy')}
                        </Typography>
                      )}
                      {it?.isBuilding && <Tag variant='primary'>Em construção</Tag>}
                    </div>
                  </Button>
                );
              })}
          </div>
        </DropDown>
      </div>

      <div className='flex h-full flex-row items-center justify-end xs:w-1/2 xs:gap-sm md:gap-lg lg:w-2/6'>
        <div className='flex'>
          <Button allowReadOnly
            onlyIcon
            size='small'
            variant='text'
            LeftIcon={'list'}
            onClick={() => {
              handleOpenContentModal();
            }}
            loading={fetchProgram.isLoading || fetchContent.isLoading}
            disabled={isContentZero || isLocked}
            className='xs:px-2 md:px-0'
          ></Button>
          <Button allowReadOnly
            size='small'
            variant='text'
            onClick={() => {
              handleOpenContentModal();
            }}
            disabled={
              isContentZero ||
              isLocked ||
              fetchProgram.isLoading ||
              fetchContent.isLoading
            }
            className='xs:hidden md:flex lg:text-sm'
          >
            Lista de conteúdos
          </Button>
        </div>
        <Button allowReadOnly
          size='small'
          className='rounded-md xs:px-2 xs:py-1 xs:text-sm md:px-4 md:py-2 lg:text-sm xl:text-md'
          onClick={handleNextContent}
          loading={fetchContent.isLoading}
          disabled={isContentZero || !nextContent}
        >
          Próximo conteúdo
        </Button>
      </div>

      <WeekProgramModal
        selectedWeek={selectedWeek}
        course={course}
        isOpen={isWeekProgramModalOpen}
        onClose={() => {
          setIsWeekProgramModalOpen(false);
        }}
      />

      {loadedCourse && course && (
        <CourseContentModal
          selectedProgramObject={loadedCourse.selectedProgramObject}
          selectedContentObject={loadedCourse.selectedContentObject}
          course={course}
          isOpen={isModalCourseContentOpen}
          onClose={() => {
            setIsModalCourseContentOpen(false);
          }}
        />
      )}
    </div>
  );
}
